import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, fromEvent, merge, of } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

export interface OfflineAction {
  id: string;
  type: string;
  data: any;
  timestamp: number;
  retryCount: number;
  maxRetries: number;
}

@Injectable({
  providedIn: 'root'
})
export class OfflineService {
  private readonly OFFLINE_ACTIONS_KEY = 'offline_actions';
  private readonly MAX_RETRY_ATTEMPTS = 3;

  private onlineSubject = new BehaviorSubject<boolean>(navigator.onLine);
  private offlineActionsSubject = new BehaviorSubject<OfflineAction[]>([]);

  public online$ = this.onlineSubject.asObservable();
  public offline$ = this.online$.pipe(map(online => !online));
  public offlineActions$ = this.offlineActionsSubject.asObservable();

  constructor() {
    this.initializeOnlineStatus();
    this.loadOfflineActions();
    this.setupOnlineListener();
  }

  /**
   * Initialize online status monitoring
   */
  private initializeOnlineStatus(): void {
    const online$ = fromEvent(window, 'online').pipe(map(() => true));
    const offline$ = fromEvent(window, 'offline').pipe(map(() => false));

    merge(online$, offline$)
      .pipe(startWith(navigator.onLine))
      .subscribe(isOnline => {
        this.onlineSubject.next(isOnline);

        if (isOnline) {
          console.log('App is online - processing offline actions');
          this.processOfflineActions();
        } else {
          console.log('App is offline - actions will be queued');
        }
      });
  }

  /**
   * Setup listener for when app comes back online
   */
  private setupOnlineListener(): void {
    this.online$.subscribe(isOnline => {
      if (isOnline) {
        // Small delay to ensure connection is stable
        setTimeout(() => {
          this.processOfflineActions();
        }, 1000);
      }
    });
  }

  /**
   * Check if app is currently online
   */
  isOnline(): boolean {
    return this.onlineSubject.value;
  }

  /**
   * Check if app is currently offline
   */
  isOffline(): boolean {
    return !this.isOnline();
  }

  /**
   * Add action to offline queue
   */
  addOfflineAction(type: string, data: any): string {
    const action: OfflineAction = {
      id: this.generateId(),
      type,
      data,
      timestamp: Date.now(),
      retryCount: 0,
      maxRetries: this.MAX_RETRY_ATTEMPTS
    };

    const actions = this.getOfflineActions();
    actions.push(action);
    this.saveOfflineActions(actions);
    this.offlineActionsSubject.next(actions);

    console.log('Added offline action:', action);
    return action.id;
  }

  /**
   * Remove action from offline queue
   */
  removeOfflineAction(actionId: string): void {
    const actions = this.getOfflineActions().filter(action => action.id !== actionId);
    this.saveOfflineActions(actions);
    this.offlineActionsSubject.next(actions);
  }

  /**
   * Get all offline actions
   */
  getOfflineActions(): OfflineAction[] {
    try {
      const stored = localStorage.getItem(this.OFFLINE_ACTIONS_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Error loading offline actions:', error);
      return [];
    }
  }

  /**
   * Save offline actions to storage
   */
  private saveOfflineActions(actions: OfflineAction[]): void {
    try {
      localStorage.setItem(this.OFFLINE_ACTIONS_KEY, JSON.stringify(actions));
    } catch (error) {
      console.error('Error saving offline actions:', error);
    }
  }

  /**
   * Load offline actions from storage
   */
  private loadOfflineActions(): void {
    const actions = this.getOfflineActions();
    this.offlineActionsSubject.next(actions);
  }

  /**
   * Process all offline actions when back online
   */
  private async processOfflineActions(): Promise<void> {
    const actions = this.getOfflineActions();

    if (actions.length === 0) {
      return;
    }

    console.log(`Processing ${actions.length} offline actions`);

    for (const action of actions) {
      try {
        await this.processAction(action);
        this.removeOfflineAction(action.id);
        console.log('Successfully processed offline action:', action.id);
      } catch (error) {
        console.error('Error processing offline action:', action.id, error);
        await this.handleActionError(action);
      }
    }
  }

  /**
   * Process a single offline action
   */
  private async processAction(action: OfflineAction): Promise<void> {
    switch (action.type) {
      case 'api_call':
        await this.processApiCall(action.data);
        break;
      case 'data_sync':
        await this.processDataSync(action.data);
        break;
      case 'file_upload':
        await this.processFileUpload(action.data);
        break;
      default:
        console.warn('Unknown offline action type:', action.type);
    }
  }

  /**
   * Process API call action
   */
  private async processApiCall(data: any): Promise<void> {
    const { url, method, body, headers } = data;

    const response = await fetch(url, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined
    });

    if (!response.ok) {
      throw new Error(`API call failed: ${response.status} ${response.statusText}`);
    }
  }

  /**
   * Process data sync action
   */
  private async processDataSync(data: any): Promise<void> {
    // Implement data synchronization logic
    console.log('Processing data sync:', data);
  }

  /**
   * Process file upload action
   */
  private async processFileUpload(data: any): Promise<void> {
    const { url, file, formData } = data;

    const response = await fetch(url, {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      throw new Error(`File upload failed: ${response.status} ${response.statusText}`);
    }
  }

  /**
   * Handle action processing error
   */
  private async handleActionError(action: OfflineAction): Promise<void> {
    action.retryCount++;

    if (action.retryCount >= action.maxRetries) {
      console.error('Max retries reached for action:', action.id);
      this.removeOfflineAction(action.id);
    } else {
      // Update the action with new retry count
      const actions = this.getOfflineActions();
      const index = actions.findIndex(a => a.id === action.id);
      if (index !== -1) {
        actions[index] = action;
        this.saveOfflineActions(actions);
        this.offlineActionsSubject.next(actions);
      }
    }
  }

  /**
   * Clear all offline actions
   */
  clearOfflineActions(): void {
    this.saveOfflineActions([]);
    this.offlineActionsSubject.next([]);
  }

  /**
   * Get offline actions count
   */
  getOfflineActionsCount(): number {
    return this.getOfflineActions().length;
  }

  /**
   * Check if there are pending offline actions
   */
  hasPendingActions(): boolean {
    return this.getOfflineActionsCount() > 0;
  }

  /**
   * Generate unique ID for actions
   */
  private generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  /**
   * Get network information (if supported)
   */
  getNetworkInfo(): any {
    const connection = (navigator as any).connection ||
                     (navigator as any).mozConnection ||
                     (navigator as any).webkitConnection;

    if (connection) {
      return {
        effectiveType: connection.effectiveType,
        downlink: connection.downlink,
        rtt: connection.rtt,
        saveData: connection.saveData
      };
    }

    return null;
  }

  /**
   * Check if connection is slow
   */
  isSlowConnection(): boolean {
    const networkInfo = this.getNetworkInfo();

    if (networkInfo) {
      return networkInfo.effectiveType === 'slow-2g' ||
             networkInfo.effectiveType === '2g' ||
             networkInfo.saveData === true;
    }

    return false;
  }
}
